import * as React from 'react';
import { Button } from "primereact/button";
import { TieredMenu } from "primereact/tieredmenu";
import { useAppDispatch, useAppSelector } from '../../store';
import { MenuItem } from 'primereact/menuitem';
import { UserRole } from '../../store/auth';
import { Dialog } from 'primereact/dialog';
import ModalButtons from '../ui/modals/ModalButtons';
import { assignGroup, markGroupAsComplete } from '../../store/group';
import { GroupStatus } from '../../utils/types';
import { getUserFullName } from '../../utils/auth';
import { groupIsAssignedToCurrentUser } from '../../utils/groupList';

export default function GroupActionsMenu({ groupName, groupId, assignedTo, timePeriodDate, requestStatus, onComplete }: { groupId: string, groupName: string, timePeriodDate: string, assignedTo: string | null | undefined, requestStatus: GroupStatus, onComplete?: () => void }) {
  const { user, role } = useAppSelector((state) => state.auth);
  const dispatch = useAppDispatch();
  const groupMenu = React.useRef<TieredMenu>();

  const [isFormLoading, setIsFormLoading] = React.useState<boolean>(false);
  const [isToggleCompletionModalVisible, setIsToggleCompletionModalVisible] = React.useState<boolean>(false);
  
  const isGroupCompleted = React.useMemo<boolean>(() => {
    return requestStatus === GroupStatus.COMPLETED;
  }, [requestStatus]);

  const menuItems = React.useMemo<MenuItem[]>(() => {
    return [
      ...(role === UserRole.ACCOUNT_MANAGER ? [
        {
          label: isGroupCompleted ? 'Re-open group' : 'Mark as complete',
          icon: isGroupCompleted ? 'pi pi-unlock' : 'pi pi-check-circle',
          command: showCompletionModal,
        }
      ] : []),
      ...(role === UserRole.ACTUARY && assignedTo !== getUserFullName(user) ? [
        {
          label: assignedTo && !groupIsAssignedToCurrentUser({ assignedTo }, user) ? 'Reassign group to me' : 'Assign group to me',
          icon: 'pi pi-user',
          command: async () => {
            dispatch(assignGroup({ groupId, timePeriodDate }));
          }
        }
      ] : [])
    ];
  }, [user, role, isGroupCompleted]);

  function toggleGroupMenu(e: React.MouseEvent<HTMLButtonElement>) {
    groupMenu.current?.toggle(e);
  }

  function showCompletionModal() {
    setIsToggleCompletionModalVisible(true);
  }

  async function submitCompletionFormRequest() {
    setIsFormLoading(true);

    dispatch(
      markGroupAsComplete({
        groupId,
        timePeriodDate,
        completed: !isGroupCompleted
      })
    );

    setIsFormLoading(false);
    setIsToggleCompletionModalVisible(false);

    if (onComplete) {
      onComplete();
    }
  } 

  if (!menuItems.length) return null;

  return (
    <div className="relative">
      <Button severity="secondary" icon="pi pi-ellipsis-h" onClick={toggleGroupMenu} />
      <TieredMenu appendTo={"self"} ref={groupMenu} model={menuItems} popup pt={{ root: { className: 'right-0 !left-auto' } } } />

      <Dialog
        draggable={false}
        visible={isToggleCompletionModalVisible}
        header={isGroupCompleted ? "Re-open group" : "Mark group as complete?"}
        onHide={() => setIsToggleCompletionModalVisible(false)}
        footer={
          <ModalButtons
            saveLabel="yes please"
            onCancel={() => setIsToggleCompletionModalVisible(false)}
            isFormValid={true}
            isLoading={isFormLoading}
            onSave={submitCompletionFormRequest}
          />
        }
      >
        {isGroupCompleted ? (
          <>Are you sure you want to re-open <strong>{groupName}</strong> group?</>
        ) : (
          <>Are you sure you want to mark the <strong>{groupName}</strong> group as complete?</>
        )}
      </Dialog>
    </div>
  )
}